import React, { useState } from 'react';
import styles from './ConnectCard.module.scss';
import lobbyStyles from './LobbyStyles.module.scss';
import ConnectInfoModal from './ConnectInfoModal';

const MAX_SLOTS = 4;

// SVG icons keyed by platform
const PlatformIcon = ({ platform }) => {
    const stroke = "currentColor";
    const props = { width: "100%", height: "100%", viewBox: "0 0 24 24", fill: "none", stroke, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
    if (platform === 'firetv' || platform === 'roku') {
        // TV icon
        return (
            <svg {...props}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
        );
    }
    if (platform === 'app') {
        // Phone icon
        return (
            <svg {...props}>
                <rect x="6" y="2" width="12" height="20" rx="2" ry="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
        );
    }
    // Default: web/browser (globe)
    return (
        <svg {...props}>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
    );
};

const platformLabel = (platform) => {
    switch (platform) {
        case 'firetv': return 'Fire TV';
        case 'roku': return 'Roku';
        case 'app': return 'Mobile App';
        default: return 'Web';
    }
};

function MirrorSlot({ mirror, index, onRemove }) {
    if (!mirror) {
        return (
            <div className={styles.slotEmpty}>
                <span className={styles.slotEmptyText}>Empty</span>
            </div>
        );
    }
    const userLabel = mirror.userEmail
        ? mirror.userEmail.split('@')[0]
        : 'Anonymous';
    const subLabel = mirror.platform === 'web' && mirror.browser
        ? `${mirror.browser} · ${mirror.os || ''}`.trim().replace(/·\s*$/, '')
        : platformLabel(mirror.platform);
    return (
        <div className={styles.slotFilled}>
            <button
                className={styles.slotRemove}
                onClick={() => onRemove(mirror.sessionId)}
                aria-label={`Remove screen ${index + 1}`}
                title="Remove screen"
            >
                &times;
            </button>
            <div className={styles.slotIcon}>
                <PlatformIcon platform={mirror.platform} />
            </div>
            <div className={styles.slotInfo}>
                <div className={styles.slotName} title={userLabel}>{userLabel}</div>
                <div className={styles.slotSub} title={subLabel}>{subLabel}</div>
            </div>
        </div>
    );
}

function ConnectCard({ mirrorCount, connectEnabled, mirrors, room }) {
    const [showInfo, setShowInfo] = useState(false);
    const [showDisableConfirm, setShowDisableConfirm] = useState(false);

    const list = mirrors ? Array.from(mirrors) : [];
    const slots = Array.from({ length: MAX_SLOTS }, (_, i) => list[i] || null);

    const handleToggle = () => {
        if (connectEnabled) {
            if (mirrorCount > 0) {
                setShowDisableConfirm(true);
                return;
            }
            sendToggle(false);
        } else {
            sendToggle(true);
        }
    };

    const sendToggle = (enabled) => {
        if (room) room.send("set_connect_enabled", { enabled });
    };

    const confirmDisable = () => {
        sendToggle(false);
        setShowDisableConfirm(false);
    };

    const removeMirror = (sessionId) => {
        if (room) room.send("remove_connect", { sessionId });
    };

    return (
        <li className={lobbyStyles.game}>
            <div className={`${styles.card} ${!connectEnabled ? styles.disabled : ''}`}>
                <div className={styles.header}>
                    <div className={styles.iconContainer}>
                        <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                            <line x1="8" y1="21" x2="16" y2="21" />
                            <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                    </div>
                    <h3 className={styles.title}>Connect</h3>

                    <label className={styles.toggleWrapper} title={connectEnabled ? "Disable Connect" : "Enable Connect"}>
                        <input
                            type="checkbox"
                            checked={connectEnabled}
                            onChange={handleToggle}
                            className={styles.toggleInput}
                        />
                        <span className={styles.toggleSlider}></span>
                    </label>

                    <button
                        className={styles.infoButton}
                        onClick={() => setShowInfo(!showInfo)}
                        aria-label="What is Connect?"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                    </button>
                </div>

                {connectEnabled ? (
                    <div className={styles.slotGrid}>
                        {slots.map((mirror, i) => (
                            <MirrorSlot
                                key={mirror?.sessionId || `empty-${i}`}
                                mirror={mirror}
                                index={i}
                                onRemove={removeMirror}
                            />
                        ))}
                    </div>
                ) : (
                    <p className={styles.disabledText}>Connect is disabled</p>
                )}

                <ConnectInfoModal show={showInfo} onHide={() => setShowInfo(false)} />

                {showDisableConfirm && (
                    <div className={styles.popup}>
                        <h4>Disable Connect?</h4>
                        <p>{mirrorCount} {mirrorCount === 1 ? "screen is" : "screens are"} currently connected. Disabling Connect will disconnect {mirrorCount === 1 ? "it" : "them"}.</p>
                        <div className={styles.popupButtons}>
                            <button className={`${styles.popupBtn} ${styles.popupBtnSecondary}`} onClick={() => setShowDisableConfirm(false)}>Cancel</button>
                            <button className={`${styles.popupBtn} ${styles.popupBtnDanger}`} onClick={confirmDisable}>Disable</button>
                        </div>
                    </div>
                )}
            </div>
        </li>
    );
}

export default ConnectCard;
